import { createContext, use, useMemo, type ReactNode } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'

import { validOperatorValue } from '@/lib/filter-values'
import { useDebouncedValue } from '@/lib/use-debounced-value'

import {
  fromFieldFilter,
  toFieldFilter,
  type DashboardContract,
  type DashboardFilterPatch,
  type FieldFilter,
  type FieldFilterValue,
  type FieldId,
  type FilterCriteria,
} from './contract'

const SEARCH_DEBOUNCE_MS = 250

export type DashboardFiltersState<Contract extends DashboardContract> = {
  contract: Contract
  /** The active filters in wire form, for requests and for grids' `baseFilters`. */
  filters: readonly FieldFilter[]
  /** The search box text, before debouncing. */
  searchInput: string
  /** What widget requests send: debounced search that meets the minimum length, the filters and the highlight. */
  criteria: FilterCriteria
  /** Fields whose filter the chart that set it draws as a selection. */
  highlight: readonly FieldId<Contract>[]
  value<Id extends FieldId<Contract>>(field: Id): FieldFilterValue<Contract['fields'][Id]> | undefined
  /**
   * Sets filters. A chart passes `highlight` with the fields it set from its own selection, so it keeps showing every
   * value and marks the selected ones while other widgets filter. Any later change ends the highlight.
   */
  set(patch: DashboardFilterPatch<Contract>, options?: { highlight?: readonly FieldId<Contract>[] }): void
  /** Sets the filter, or clears it when it already has exactly this value. */
  toggle<Id extends FieldId<Contract>>(field: Id, value: FieldFilterValue<Contract['fields'][Id]>): void
  /** Replaces every filter at once, as a filter bar does. */
  replace(filters: readonly FieldFilter[]): void
  setSearch(value: string): void
  clear(): void
}

const DashboardFiltersContext = createContext<DashboardFiltersState<DashboardContract> | null>(null)

/** Keys this provider owns in the route search. Routes keep them through `normalizeDashboardSearch`. */
export type DashboardSearch = { q?: string; filters?: string; highlight?: string }

/**
 * Holds one dashboard's filters in the URL and gives them to every widget below it.
 * Widgets read them with `useDashboardFilters` and send `criteria` with their own requests.
 */
export function DashboardFilters<Contract extends DashboardContract>({
  contract,
  children,
}: {
  contract: Contract
  children: ReactNode
}) {
  const search = useSearch({ strict: false }) as DashboardSearch
  const navigate = useNavigate()
  const filters = useMemo(() => parseFilters(search.filters, contract), [search.filters, contract])
  const highlight = useMemo(() => parseHighlight(search.highlight, filters), [search.highlight, filters])
  const searchInput = search.q ?? ''
  const debounced = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS).trim()

  const state = useMemo<DashboardFiltersState<Contract>>(() => {
    const write = (next: readonly FieldFilter[], q = searchInput, highlighted: readonly string[] = []) =>
      void navigate({
        to: '.',
        replace: true,
        search: (previous: Record<string, unknown>) => ({
          ...previous,
          q: q || undefined,
          filters: next.length ? JSON.stringify(next) : undefined,
          highlight: highlighted.length ? highlighted.join(',') : undefined,
        }),
      } as never)
    const valueOf = (field: string) => {
      const filter = filters.find((item) => item.field === field)
      return filter ? fromFieldFilter(filter) : undefined
    }
    const applyPatch = (patch: Record<string, object | undefined>, highlighted: readonly string[] = []) => {
      const fields = Object.keys(patch)
      const kept = filters.filter((filter) => !fields.includes(filter.field))
      const added = Object.entries(patch).flatMap(([field, value]) => (value ? [toFieldFilter(field, value)] : []))
      write([...kept, ...added], searchInput, highlighted.filter((field) => patch[field]))
    }
    const minimum = contract.searchMinimumLength
    return {
      contract,
      filters,
      searchInput,
      criteria: {
        search: minimum !== null && debounced.length >= minimum ? debounced : undefined,
        filters: filters.length ? [...filters] : undefined,
        highlight: highlight.length ? [...highlight] : undefined,
      },
      highlight: highlight as FieldId<Contract>[],
      value: (field) => valueOf(field) as never,
      set: (patch, options) => applyPatch(patch as Record<string, object | undefined>, options?.highlight),
      toggle: (field, value) =>
        applyPatch({ [field]: JSON.stringify(valueOf(field)) === JSON.stringify(value) ? undefined : value }),
      replace: (next) => write(next),
      setSearch: (value) => write(filters, value),
      clear: () => write([], ''),
    }
  }, [contract, filters, highlight, searchInput, debounced, navigate])

  return <DashboardFiltersContext value={state as unknown as DashboardFiltersState<DashboardContract>}>{children}</DashboardFiltersContext>
}

export function useDashboardFilters<Contract extends DashboardContract = DashboardContract>() {
  const state = use(DashboardFiltersContext)
  if (!state) throw new Error('useDashboardFilters must be used inside <DashboardFilters>.')
  return state as unknown as DashboardFiltersState<Contract>
}

/**
 * Runs a widget's request with the dashboard criteria and refetches when they change.
 * Widgets that share a key share one request.
 */
export function useDashboardQuery<Data>({
  key,
  fetch,
}: {
  key: readonly unknown[]
  fetch: (criteria: FilterCriteria, signal: AbortSignal) => Promise<Data>
}) {
  const { criteria } = useDashboardFilters()
  return useQuery({
    queryKey: [...key, criteria],
    queryFn: ({ signal }) => fetch(criteria, signal),
    placeholderData: keepPreviousData,
  })
}

/** Keeps a route's dashboard keys valid: drops malformed JSON and filters with an unknown field, operator or value. */
export function normalizeDashboardSearch(search: Record<string, unknown>, contract: DashboardContract): DashboardSearch {
  const q = typeof search.q === 'string' && search.q ? search.q : undefined
  const filters = parseFilters(typeof search.filters === 'string' ? search.filters : undefined, contract)
  const highlight = parseHighlight(typeof search.highlight === 'string' ? search.highlight : undefined, filters)
  return {
    q,
    filters: filters.length ? JSON.stringify(filters) : undefined,
    highlight: highlight.length ? highlight.join(',') : undefined,
  }
}

/** Keeps only highlighted fields that still have a filter. */
function parseHighlight(raw: string | undefined, filters: readonly FieldFilter[]): string[] {
  if (!raw) return []
  return raw.split(',').filter((field) => filters.some((filter) => filter.field === field))
}

function parseFilters(raw: string | undefined, contract: DashboardContract): FieldFilter[] {
  if (!raw) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    const seen = new Set<string>()
    return parsed.filter((item): item is FieldFilter => {
      if (!item || typeof item !== 'object') return false
      const { field, operator, values } = item as Partial<FieldFilter>
      const schema = typeof field === 'string' ? contract.fields[field] : undefined
      if (!schema || typeof operator !== 'string' || !schema.operators.includes(operator) || seen.has(field!)) return false
      if (!validOperatorValue(operator, values, schema.kind)) return false
      seen.add(field!)
      return true
    })
  } catch {
    return []
  }
}
